import { useEffect, useMemo, useState } from 'react';
import {
  BriefcaseBusiness,
  ClipboardList,
  ContactRound,
  ChevronLeft,
  FileCheck2,
  FileText,
  Gauge,
  IdCard,
  LogOut,
  Menu,
  MessageSquareText,
  PanelLeftClose,
  PanelLeftOpen,
  Settings,
  ShieldCheck,
  UserRound,
  X,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { productConfig } from '@shared/config';
import { useAuth } from '@/app/auth-context';
import { BrandLogo } from '@/components/brand/brand-logo';
import { LanguageToggle } from '@/components/navigation/language-toggle';
import { Badge, Button } from '@/components/ui';
import { cn } from '@/lib/cn';
import { routes } from '@/lib/routes';

const mainNavigation = [
  { key: 'overview', href: routes.app, icon: Gauge, end: true },
  { key: 'intake', href: routes.intake, icon: ClipboardList },
  { key: 'profile', href: routes.profile, icon: IdCard },
  { key: 'cvBuilder', href: routes.cv, icon: FileText },
  { key: 'cvCheck', href: routes.cvCheck, icon: FileCheck2 },
  { key: 'linkedin', href: routes.linkedin, icon: ContactRound },
  { key: 'jobMatch', href: routes.jobs, icon: BriefcaseBusiness },
  { key: 'interview', href: routes.interview, icon: MessageSquareText },
] as const;

const mobileNavigation = [
  mainNavigation[0],
  mainNavigation[3],
  mainNavigation[5],
  mainNavigation[6],
  mainNavigation[7],
];

function initials(name: string): string {
  return (
    name
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? '')
      .join('') || 'CL'
  );
}

export function AppShell() {
  const { t } = useTranslation('common');
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(
    () => window.localStorage.getItem('careerlaunch-sidebar') === 'collapsed',
  );
  const [mobileMenu, setMobileMenu] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const navItems = useMemo(
    () =>
      user?.role === 'admin'
        ? [...mainNavigation, { key: 'admin' as const, href: routes.admin, icon: ShieldCheck }]
        : mainNavigation,
    [user?.role],
  );

  useEffect(() => {
    const titleKey =
      mainNavigation.find(({ href }) => location.pathname === href)?.key ??
      (location.pathname.startsWith(routes.account)
        ? 'account'
        : location.pathname.startsWith(routes.admin)
          ? 'admin'
          : 'overview');
    document.title = `${t(`appNav.${titleKey}`)} | ${productConfig.brandName}`;
  }, [location.pathname, t]);

  const toggleCollapsed = () => {
    setCollapsed((current) => {
      const next = !current;
      window.localStorage.setItem('careerlaunch-sidebar', next ? 'collapsed' : 'expanded');
      return next;
    });
  };

  const handleLogout = async () => {
    setLoggingOut(true);
    try {
      await logout();
      void navigate(routes.home, { replace: true, flushSync: true });
    } catch {
      toast.error(t('status.pageErrorBody'));
    } finally {
      setLoggingOut(false);
    }
  };

  return (
    <div className={cn('app-shell', collapsed && 'app-shell--collapsed')}>
      <a href="#workspace-content" className="skip-link">
        {t('skipToContent')}
      </a>
      <aside className="app-sidebar" aria-label={t('navigation.workspace')}>
        <div className="app-sidebar__top">
          <BrandLogo compact={collapsed} />
          <Button
            variant="ghost"
            size="icon"
            className="app-sidebar__collapse"
            onClick={toggleCollapsed}
            aria-label={collapsed ? t('appNav.expand') : t('appNav.collapse')}
          >
            {collapsed ? <PanelLeftOpen size={19} /> : <PanelLeftClose size={19} />}
          </Button>
        </div>
        <nav className="app-sidebar__nav">
          {navItems.map(({ key, href, icon: Icon, ...item }) => (
            <NavLink
              key={key}
              to={href}
              end={'end' in item ? item.end : undefined}
              className={({ isActive }) => cn('app-nav-link focus-ring', isActive && 'is-active')}
              title={collapsed ? t(`appNav.${key}`) : undefined}
            >
              <Icon size={20} aria-hidden="true" />
              <span>{t(`appNav.${key}`)}</span>
            </NavLink>
          ))}
        </nav>
        <div className="app-sidebar__bottom">
          <NavLink
            to={routes.account}
            className={({ isActive }) => cn('sidebar-account focus-ring', isActive && 'is-active')}
          >
            <span className="avatar">{initials(user?.fullName ?? '')}</span>
            <span className="sidebar-account__details">
              <strong>{user?.fullName}</strong>
              <small>{user?.email}</small>
            </span>
            {!collapsed && <ChevronLeft className="rtl-flip" size={17} />}
          </NavLink>
        </div>
      </aside>

      <div className="app-workspace">
        <header className="app-header">
          <div className="app-header__mobile-brand">
            <BrandLogo />
          </div>
          <div className="app-header__spacer" />
          <Badge tone="success" className="beta-badge">
            {t('status.beta')}
          </Badge>
          <LanguageToggle compact />
          <NavLink
            className="app-header__account focus-ring"
            to={routes.account}
            aria-label={t('actions.viewAccount')}
          >
            <UserRound size={19} />
            <span>{user?.fullName.split(' ')[0]}</span>
          </NavLink>
          <Button
            className="app-header__logout"
            variant="ghost"
            size="icon"
            disabled={loggingOut}
            onClick={() => void handleLogout()}
            aria-label={t('actions.signOut')}
          >
            <LogOut size={19} />
          </Button>
          <Button
            className="app-header__menu"
            variant="ghost"
            size="icon"
            onClick={() => setMobileMenu((current) => !current)}
            aria-expanded={mobileMenu}
            aria-controls="app-mobile-drawer"
            aria-label={
              mobileMenu ? t('navigation.closeWorkspaceMenu') : t('navigation.openWorkspaceMenu')
            }
          >
            {mobileMenu ? <X size={21} /> : <Menu size={21} />}
          </Button>
        </header>

        {mobileMenu && (
          <nav
            id="app-mobile-drawer"
            className="app-mobile-drawer"
            aria-label={t('navigation.more')}
          >
            <NavLink to={routes.intake} onClick={() => setMobileMenu(false)}>
              <ClipboardList size={19} /> {t('appNav.intake')}
            </NavLink>
            <NavLink to={routes.profile} onClick={() => setMobileMenu(false)}>
              <IdCard size={19} /> {t('appNav.profile')}
            </NavLink>
            <NavLink to={routes.cvCheck} onClick={() => setMobileMenu(false)}>
              <FileCheck2 size={19} /> {t('appNav.cvCheck')}
            </NavLink>
            <NavLink to={routes.account} onClick={() => setMobileMenu(false)}>
              <Settings size={19} /> {t('appNav.account')}
            </NavLink>
          </nav>
        )}

        <main id="workspace-content" className="app-content">
          <Outlet />
        </main>
      </div>

      <nav className="mobile-bottom-nav" aria-label={t('navigation.shortcuts')}>
        {mobileNavigation.map(({ key, href, icon: Icon, ...item }) => (
          <NavLink
            key={key}
            to={href}
            end={'end' in item ? item.end : undefined}
            className={({ isActive }) =>
              cn('mobile-bottom-nav__link focus-ring', isActive && 'is-active')
            }
          >
            <Icon size={20} aria-hidden="true" />
            <span>{t(`appNav.${key}`)}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
